import type { NoteName, Scale } from '@tunasong/schemas'
import type { NoteDegree } from '../note/note-lib.js'
import { getNote } from '../note/note-util.js'
import ScaleLib from './scale.js'

export interface ComplementaryScales {
  major: NoteName
  minor: NoteName
  // If a scale is specified, the degrees of the major and minor chords
  majorDegree?: NoteDegree
  majorDegreeName?: string
  minorDegree?: NoteDegree
  minorDegreeName?: string
}

/**
 * Generates the circle of fifths as an array of complementary scales, optionally based on a given scale.
 *
 * Each entry in the returned array represents a step around the circle of fifths, containing information
 * about the major and minor keys at that position, as well as their corresponding scale degrees and names
 * if applicable.
 *
 * @param scale - (Optional) The scale to use for determining intervals, chord types, and degree names.
 *                If not provided, the function will use default note relationships.
 * @returns An array of `ComplementaryScales` objects, each representing a position in the circle of fifths,
 *          including major and minor key roots, their degrees, and their names if they match the expected
 *          chord types.
 */
export const circleOfFifths = (scale?: Scale) => {
  const circle: ComplementaryScales[] = []

  const scaleSpec = scale ? ScaleLib.getScaleSpec(scale) : undefined
  let note: NoteName = 'C'
  for (let i = 0; i < 12; i++) {
    const majorKeyRoot = getNote(note, 'I') // No-op, but here for completeness
    const majorNoteInterval = scale ? ScaleLib.getInterval(scale, majorKeyRoot) : undefined
    const majorDegree = majorNoteInterval ? ScaleLib.noteDegreeToScaleDegree(majorNoteInterval) : undefined
    const minorKeyRoot = getNote(note, 'VI')
    const minorNoteInterval = scale ? ScaleLib.getInterval(scale, minorKeyRoot) : undefined
    const minorDegree = minorNoteInterval ? ScaleLib.noteDegreeToScaleDegree(minorNoteInterval) : undefined

    // We get the chord of the degree to determine whether this is a major or minor degree
    const majorDegreeName =
      scale && majorDegree
        ? ScaleLib.prettyScaleChordDegree(scale, ScaleLib.noteDegreeToScaleDegree(majorDegree))
        : undefined
    const majorDegreeChordType = majorDegree ? scaleSpec?.chordTypes?.[majorDegree] : undefined
    const isMajor = majorDegreeChordType === ''

    const minorDegreeName =
      scale && minorDegree
        ? ScaleLib.prettyScaleChordDegree(scale, ScaleLib.noteDegreeToScaleDegree(minorDegree))
        : undefined
    const minorDegreeChordType = minorDegree ? scaleSpec?.chordTypes?.[minorDegree] : undefined
    // Inner-ring badges are *relative minors only* — the wheel has no slot
    // for diminished triads. vii° (C major) or ii° (A minor) describes a
    // diminished chord whose root lands on the relative-minor letter, but
    // the displayed inner-ring chord at that slot is the relative MINOR,
    // not the diminished. So we omit the badge to avoid mislabeling.
    const isMinor = minorDegreeChordType === 'm'

    circle.push({
      major: majorKeyRoot,
      minor: minorKeyRoot,
      majorDegree: isMajor ? majorDegree : undefined,
      majorDegreeName: isMajor ? majorDegreeName : undefined,
      minorDegree: isMinor ? minorDegree : undefined,
      minorDegreeName: isMinor ? minorDegreeName : undefined,
    })
    note = getNote(note, 'V')
  }
  return circle
}
